import { Module } from 'vuex';
import { EquipmentsState } from '@/modules/equipments/store/equipments.state';
import { EquipmentsGetters } from '@/modules/equipments/store/equipments.getters';
import { EquipmentsResult } from '@/modules/equipments/domain/equipments.result';
import { EquipmentsMutations } from '@/modules/equipments/store/equipments.mutations';
import { EquipmentsActions } from '@/modules/equipments/store/equipments.actions';
import { EquipmentOptions } from '@/modules/equipments/domain/equipment-options';
import equipmentRepository from '@/modules/equipments/repositories/equipments.repository';
import reportRepository from '@/modules/reports/shared/repositories/reports.repository';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import { EquipmentRequest } from '@/modules/equipments/domain/equipment-request';
import { DatacenterEquipments } from '@/modules/reports/shared/domain/datacenter-equipments';
import { Parameter } from '@/modules/equipments/domain/parameter';
import { NewEquipment } from '@/modules/equipments/domain/new-equipment';

const equipments: Module<EquipmentsState, any> = {
    namespaced: true,
    state: {
        dashboardView: {
            equipments: {
                limit: 0,
                start: 0,
                pages: 0,
                total: 0,
                results: [],
            },
            buildings: [],
            parameters: [],
        },
    },
    getters: {
        [EquipmentsGetters.GET_EQUIPMENTS](state): EquipmentsResult {
            return state.dashboardView.equipments;
        },
        [EquipmentsGetters.GET_DATACENTERS](state): DatacenterEquipments[] {
            return state.dashboardView.buildings;
        },
        [EquipmentsGetters.GET_PARAMETERS](state): Parameter[] {
            return state.dashboardView.parameters;
        },
    },
    mutations: {
        [EquipmentsMutations.SET_EQUIPMENTS](state, equipments: EquipmentsResult): void {
            state.dashboardView.equipments = equipments;
        },
        [EquipmentsMutations.SET_DATACENTERS](state, datacenters: DatacenterEquipments[]): void {
            state.dashboardView.buildings = datacenters;
        },
        [EquipmentsMutations.SET_PARAMETERS](state, parameters: Parameter[]): void {
            state.dashboardView.parameters = parameters;
        },
    },
    actions: {
        async [EquipmentsActions.FETCH_EQUIPMENTS](
            { commit },
            options: EquipmentOptions,
        ): Promise<RequestResponse> {
            try {
                const equipments = await equipmentRepository.getEquipments(options);
                commit(EquipmentsMutations.SET_EQUIPMENTS, equipments);
                return false;
            } catch (e) {
                return new Exception(e);
            }
        },

        async [EquipmentsActions.UPDATE_EQUIPMENT](
            {},
            newEquipment: NewEquipment,
        ): Promise<RequestResponse> {
            try {
                let transformedEquipment: EquipmentRequest = {
                    id: newEquipment.id,
                    name: newEquipment.name,
                    buildingDataCenters: [],
                };
                if (
                    newEquipment.buildingsDataCentersSelected &&
                    newEquipment.buildingsDataCentersSelected.length
                ) {
                    newEquipment.buildingsDataCentersSelected.forEach(buildingsDataCenter => {
                        let buildingsDataCenterAux: {
                            id: number;
                            parameters: number[];
                        } = {
                            id: buildingsDataCenter,
                            parameters: newEquipment.parametersSelected,
                        };

                        transformedEquipment.buildingDataCenters.push(buildingsDataCenterAux);
                    });
                }
                await equipmentRepository.updateEquipment(transformedEquipment);
                return false;
            } catch (e) {
                return new Exception(e);
            }
        },

        async [EquipmentsActions.CREATE_EQUIPMENT](
            {},
            newEquipment: NewEquipment,
        ): Promise<RequestResponse> {
            try {
                let transformedEquipment: EquipmentRequest = {
                    name: newEquipment.name,
                    buildingDataCenters: [],
                };
                if (
                    newEquipment.buildingsDataCentersSelected &&
                    newEquipment.buildingsDataCentersSelected.length
                ) {
                    newEquipment.buildingsDataCentersSelected.forEach(buildingsDataCenter => {
                        let buildingsDataCenterAux: {
                            id: number;
                            parameters: number[];
                        } = {
                            id: buildingsDataCenter,
                            parameters: newEquipment.parametersSelected,
                        };

                        transformedEquipment.buildingDataCenters.push(buildingsDataCenterAux);
                    });
                }
                await equipmentRepository.createEquipment(transformedEquipment);
                return false;
            } catch (e) {
                return new Exception(e);
            }
        },

        async [EquipmentsActions.DELETE_EQUIPMENT](
            {},
            buildingEquipmentId: number,
        ): Promise<RequestResponse> {
            try {
                await equipmentRepository.deleteEquipment(buildingEquipmentId);
                return false;
            } catch (e) {
                return new Exception(e);
            }
        },

        async [EquipmentsActions.FETCH_DATACENTERS]({ commit }): Promise<RequestResponse> {
            try {
                const buildings = await reportRepository.getDataCentersAndBuildings();
                commit(EquipmentsMutations.SET_DATACENTERS, buildings);
                return false;
            } catch (e) {
                return new Exception(e);
            }
        },

        async [EquipmentsActions.FETCH_PARAMETERS]({ commit }): Promise<RequestResponse> {
            try {
                const parameters = await equipmentRepository.getParameters();
                commit(EquipmentsMutations.SET_PARAMETERS, parameters);
                return false;
            } catch (e) {
                return new Exception(e);
            }
        },
    },
};

export default equipments;
